/**
 * Pure pixel-level repair operations.
 *
 * Everything here works on raw RGBA buffers (no DOM / canvas APIs) so the
 * repair math can be unit-tested in Node:
 *
 *  - `autoCropAndPadToFill`  -> the Pro "Auto-Crop & Pad to Exact 86% Fill" toggle
 *  - `fitIntoCanvas`         -> aspect-preserving white padding
 *  - `fitIntoPreset`         -> marketplace preset sizing (2000x2000 / 2000x1600 / 2048x2048)
 */

import {
  computeFillRatio,
  DEFAULT_BACKGROUND_THRESHOLD,
  findProductBoundingBox,
  hasProductContent,
  resizePixels,
  type BoundingBox
} from './image-auditor.ts';

export const WHITE: readonly [number, number, number, number] = [255, 255, 255, 255];

export interface PixelBuffer {
  pixels: Uint8ClampedArray;
  width: number;
  height: number;
}

export interface AutoFillResult extends PixelBuffer {
  /** Fill ratio actually achieved (may be clamped for very elongated products) */
  achievedFill: number;
  /** Requested target (default 86) */
  targetFill: number;
  /** Product box before cropping */
  sourceBox: BoundingBox;
  /** True when the buffer was rewritten */
  applied: boolean;
  /** Human-readable note when the exact target could not be reached */
  note?: string;
}

/** Creates a solid white RGBA buffer of the given size. */
export function createWhiteBuffer(width: number, height: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < out.length; i += 4) {
    out[i] = WHITE[0];
    out[i + 1] = WHITE[1];
    out[i + 2] = WHITE[2];
    out[i + 3] = WHITE[3];
  }
  return out;
}

/** Pastes `src` centered onto a white canvas of `canvasWidth` x `canvasHeight`. */
export function pasteCentered(
  src: Uint8ClampedArray,
  srcWidth: number,
  srcHeight: number,
  canvasWidth: number,
  canvasHeight: number
): PixelBuffer {
  const canvas = createWhiteBuffer(canvasWidth, canvasHeight);
  const offsetX = Math.floor((canvasWidth - srcWidth) / 2);
  const offsetY = Math.floor((canvasHeight - srcHeight) / 2);

  for (let y = 0; y < srcHeight; y++) {
    const cy = y + offsetY;
    if (cy < 0 || cy >= canvasHeight) continue;
    for (let x = 0; x < srcWidth; x++) {
      const cx = x + offsetX;
      if (cx < 0 || cx >= canvasWidth) continue;
      const s = (y * srcWidth + x) * 4;
      const d = (cy * canvasWidth + cx) * 4;
      canvas[d] = src[s];
      canvas[d + 1] = src[s + 1];
      canvas[d + 2] = src[s + 2];
      canvas[d + 3] = src[s + 3];
    }
  }

  return { pixels: canvas, width: canvasWidth, height: canvasHeight };
}

/** Crops a sub-rectangle out of a larger buffer. */
export function cropRegion(
  pixels: Uint8ClampedArray,
  width: number,
  _height: number,
  box: BoundingBox
): PixelBuffer {
  const out = new Uint8ClampedArray(box.width * box.height * 4);
  for (let y = 0; y < box.height; y++) {
    const srcRow = (box.minY + y) * width + box.minX;
    const dstRow = y * box.width;
    for (let x = 0; x < box.width; x++) {
      const s = (srcRow + x) * 4;
      const d = (dstRow + x) * 4;
      out[d] = pixels[s];
      out[d + 1] = pixels[s + 1];
      out[d + 2] = pixels[s + 2];
      out[d + 3] = pixels[s + 3];
    }
  }
  return { pixels: out, width: box.width, height: box.height };
}

/**
 * Pro repair: tightly crop the detected product, then pad it with pure white on
 * a square canvas until the product box occupies EXACTLY `targetFill` percent
 * of the canvas area (spec default: 86%).
 *
 * Geometry notes:
 *  - For a box of area A inside a square canvas of side S, fill = A / S^2.
 *  - S is therefore sqrt(A / target) - but a product box can never be larger
 *    than the canvas, so extremely elongated products are clamped to the best
 *    achievable fill (area / max(w,h)^2) instead of being distorted.
 *  - S is rounded to whichever integer side lands closest to the exact target.
 */
export function autoCropAndPadToFill(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  targetFill = 86
): AutoFillResult {
  const sourceBox = findProductBoundingBox(pixels, width, height);
  const currentFill = computeFillRatio(sourceBox, width, height);
  const target = Math.min(Math.max(targetFill, 1), 100);

  // Nothing to crop: image is effectively all background.
  if (!hasProductContent(pixels, width, height, DEFAULT_BACKGROUND_THRESHOLD) || sourceBox.width <= 0 || sourceBox.height <= 0) {
    return {
      pixels: new Uint8ClampedArray(pixels),
      width,
      height,
      achievedFill: currentFill,
      targetFill: target,
      sourceBox,
      applied: false
    };
  }

  const area = sourceBox.width * sourceBox.height;
  const maxDim = Math.max(sourceBox.width, sourceBox.height);
  const maxFill = (area / (maxDim * maxDim)) * 100;
  const effectiveTarget = Math.min(target, maxFill);

  const idealSide = Math.sqrt(area / (effectiveTarget / 100));
  const candidates = new Set<number>([Math.round(idealSide), Math.floor(idealSide), Math.ceil(idealSide), maxDim]);
  let side = maxDim;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const s = Math.max(candidate, maxDim, 1);
    const fill = (area / (s * s)) * 100;
    const delta = Math.abs(fill - effectiveTarget);
    if (delta < bestDelta) {
      bestDelta = delta;
      side = s;
    }
  }

  const crop = cropRegion(pixels, width, height, sourceBox);
  const padded = pasteCentered(crop.pixels, crop.width, crop.height, side, side);
  const achievedFill = round2((area / (side * side)) * 100);

  const clamped = maxFill < target - 0.01;
  return {
    ...padded,
    achievedFill,
    targetFill: target,
    sourceBox,
    applied: !(padded.width === width && padded.height === height && samePixels(padded.pixels, pixels)),
    note: clamped
      ? `Product aspect ratio caps the achievable fill at ${round2(maxFill)}%.`
      : undefined
  };
}

/** Scales a buffer to fit (contain) inside `canvasWidth` x `canvasHeight`, padding with white. */
export function fitIntoPreset(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  canvasWidth: number,
  canvasHeight: number
): PixelBuffer {
  if (width <= 0 || height <= 0) throw new Error('fitIntoPreset: invalid source size');
  const scale = Math.min(canvasWidth / width, canvasHeight / height);
  const targetW = Math.max(1, Math.round(width * scale));
  const targetH = Math.max(1, Math.round(height * scale));
  const scaled = resizePixels(pixels, width, height, targetW, targetH);
  return pasteCentered(scaled.pixels, scaled.width, scaled.height, canvasWidth, canvasHeight);
}

/** Convenience wrapper mirroring `fitIntoPreset` with an explicit canvas buffer. */
export function fitIntoCanvas(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  canvasWidth: number,
  canvasHeight: number
): PixelBuffer {
  return fitIntoPreset(pixels, width, height, canvasWidth, canvasHeight);
}

function samePixels(a: Uint8ClampedArray, b: Uint8ClampedArray): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
